import {useState} from "react";
import {Alert, Badge, Button, Card, Table} from "react-bootstrap";
import {Link} from "react-router";
import {ProfileApi} from "../../api/ProfileApi";
import {LicenseActivationViewModel, LicenseViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {ConfirmModal} from "../../components/ConfirmModal";
import {CopyButton} from "../../components/CopyButton";
import {formatDate, formatDateTime} from "../../components/format";
import {TrademarkNoticesFor} from "../../components/TrademarkNotices";
import {account, format, rich} from "../../i18n";

// The account page the products link to (Astrel's TSA1_LICENSE_PORTAL): see and free the computers using your licenses, and download.
export function MyLicensesPage() {
    const {data: licenses, error, loading, reload} = useAsync(() => ProfileApi.licenses(), []);
    const [freeing, setFreeing] = useState<{ license: LicenseViewModel, activation: LicenseActivationViewModel }>();
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<unknown>();

    const free = async () => {
        if (!freeing) {
            return;
        }
        setBusy(true);
        setActionError(undefined);
        try {
            await ProfileApi.freeSeat(freeing.license.id, freeing.activation.id);
            reload();
        } catch (err) {
            setActionError(err);
        } finally {
            setBusy(false);
            setFreeing(undefined);
        }
    };

    return (
        <>
            <PageHeader title={account.licenses.title}/>
            {loading && !licenses && <LoadingSpinner/>}
            <ErrorAlert error={error}/>
            <ErrorAlert error={actionError} messages={{ 404: account.licenses.alreadyDeactivated }} onClose={() => setActionError(undefined)}/>
            {licenses?.length === 0 && <Alert variant="secondary">{account.licenses.none}</Alert>}
            {licenses?.map(license => (
                <Card key={license.id} className="mb-4">
                    <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                        <strong>{license.productName || account.licenses.unknownProduct}</strong>
                        {license.kind === 'earlyAccess'
                            ? <Badge bg="info" text="dark" title={account.licenses.earlyAccessHint}>{account.licenses.earlyAccess}</Badge>
                            : license.kind === 'review'
                                ? <Badge bg="secondary">{account.licenses.review}</Badge>
                                : <Badge bg="secondary">{license.edition}</Badge>}
                        {license.kind === 'review' && !license.revoked && license.reviewExpiresOn && (
                            <span className="small text-body-secondary">
                                {license.reviewKept ? account.licenses.reviewKept : format(account.licenses.reviewUntil, { date: formatDate(license.reviewExpiresOn) })}
                            </span>
                        )}
                        {license.revoked && <Badge bg="danger">{account.licenses.revoked}</Badge>}
                        <span className="ms-auto d-flex align-items-center gap-2">
                            <span className="license-key">{license.key}</span>
                            <CopyButton text={license.key} label={account.licenses.copyKey}/>
                        </span>
                    </Card.Header>
                    <Card.Body>
                        {license.productSku && !license.revoked && (
                            <Link to={`/products/${encodeURIComponent(license.productSku)}/download`} className="btn btn-outline-primary btn-sm float-end ms-3 mb-2">
                                <i className="bi bi-download me-1"></i>{account.licenses.downloads}
                            </Link>
                        )}
                        <p className="mb-3">
                            {rich(account.licenses.summary, { strong: text => <strong>{text}</strong> }, {
                                licensee: license.licensee, active: license.seatsInUse, seats: license.seats,
                                used: license.activationsThisYear, perYear: license.activationsPerYear
                            })}
                        </p>
                        {license.activations.length === 0 ? (
                            <p className="text-body-secondary mb-0">{account.licenses.noActivations}</p>
                        ) : (
                            <Table responsive className="mb-0">
                                <thead>
                                <tr><th>{account.licenses.columns.computer}</th><th>{account.licenses.columns.system}</th><th>{account.licenses.columns.version}</th><th>{account.licenses.columns.lastSeen}</th><th></th></tr>
                                </thead>
                                <tbody>
                                {license.activations.map(activation => (
                                    <tr key={activation.id}>
                                        <td>{activation.machineName || account.licenses.unnamedComputer}</td>
                                        <td className="small text-body-secondary">{activation.os}</td>
                                        <td>
                                            {activation.productVersion}
                                            {activation.devBuild && <Badge bg="secondary" className="ms-2" title={account.licenses.devBuildHint}>{account.licenses.devBuild}</Badge>}
                                        </td>
                                        <td>{formatDateTime(activation.lastSeen)}</td>
                                        <td className="text-end">
                                            <Button size="sm" variant="outline-danger" onClick={() => setFreeing({ license, activation })}>{account.licenses.deactivate}</Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            ))}
            {!!licenses?.length && <TrademarkNoticesFor skus={licenses.map(x => x.productSku)} className="mt-5"/>}
            <ConfirmModal show={!!freeing} title={account.licenses.confirm.title} confirmLabel={account.licenses.deactivate} busy={busy} onConfirm={free} onCancel={() => setFreeing(undefined)}>
                <p>
                    {rich(account.licenses.confirm.text, { strong: text => <strong>{text}</strong> }, {
                        computer: freeing?.activation.machineName || account.licenses.confirm.thisComputer,
                        product: freeing?.license.productName || account.licenses.confirm.theProduct
                    })}
                </p>
                <p className="mb-0 text-body-secondary">
                    {rich(account.licenses.confirm.note, { em: text => <em>{text}</em> }, { perYear: freeing?.license.activationsPerYear ?? '' })}
                </p>
            </ConfirmModal>
        </>
    );
}
